import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import {
    EscalatedToDisplayTextMap,
    RequestChatIcon,
    RequestType,
    UserCell,
} from '@strata/react-chat';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { PayerRequestStatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { PayerRequestNumber } from 'components/requests/PayerRequestNumber';
import { ViewsControls } from 'components/shared/ViewsControls';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useUserNameMap } from 'store/api/authApi';
import { type OpenRequestSummary, useGetOpenRequestsByTypeQuery } from 'store/api/payerRequestApi';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'createDate', desc: true }];

const customSearchValueHandlers: ComponentProps<
    typeof GridWithFilters<OpenRequestSummary>
>['customSearchValueHandlers'] = {
    createDate: (value) => formatDate(value, DateFormats.DATE_TIME),
    requestStatus: (value) => PayerRequestStatusIndicator.StatusMessages[value],
};

interface RequestsGridProps {
    requestType: Exclude<
        (typeof RequestType)[keyof typeof RequestType],
        typeof RequestType.ARCHIVE
    >;
}

const filterCategories = (
    { requestType }: RequestsGridProps,
    subSubTypeFeatureFlag: boolean,
): FilterCategory<OpenRequestSummary>[] => [
    ...(requestType !== RequestType.OTHER
        ? [{ key: 'requestSubType', title: 'Request Type' } as FilterCategory<OpenRequestSummary>]
        : []),
    {
        key: 'requestStatus',
        title: 'Status',
        valueDisplayTextMap: PayerRequestStatusIndicator.StatusMessages,
    },
    ...(subSubTypeFeatureFlag && requestType === RequestType.ESCALATION
        ? [{ key: 'typeLevel2', title: 'Request Subtype' } as FilterCategory<OpenRequestSummary>]
        : []),
    { key: 'assignedFullName', title: 'FMH Assignee' },
    { key: 'payerAssignedFullName', title: 'Payer Assignee' },
    {
        key: 'urgent',
        title: 'Request Urgency',
        customValues: [{ value: 'true', text: 'Urgent' }],
    },
    {
        key: 'pbmOnly',
        title: 'Visibility',
        customValues: [{ value: 'true', text: 'PBM Only' }],
    },
];

const columns = (
    { requestType }: RequestsGridProps,
    subSubTypeFeatureFlag: boolean,
): ColumnDef<OpenRequestSummary>[] => [
    {
        ...{
            [RequestType.ESCALATION]: { accessorKey: 'memberId', header: 'Member ID' },
            [RequestType.CHANGE]: { accessorKey: 'requestSubType', header: 'Request Type' },
            [RequestType.OTHER]: { accessorKey: 'summary', header: 'Summary' },
        }[requestType],
        size: 180,
        cell: ({ getValue }: { getValue: () => string | undefined }) => (
            <PrimaryData data={getValue()} />
        ),
    } as ColumnDef<OpenRequestSummary>,
    {
        ...{
            [RequestType.ESCALATION]: { accessorKey: 'requestSubType', header: 'Request Type' },
            [RequestType.CHANGE]: { accessorKey: 'summary', header: 'Summary' },
            [RequestType.OTHER]: { accessorKey: 'additionalNotes', header: 'Description' },
        }[requestType],
        size: 180,
    } as ColumnDef<OpenRequestSummary>,
    ...(subSubTypeFeatureFlag && requestType === RequestType.ESCALATION
        ? [
              {
                  accessorKey: 'typeLevel2',
                  header: 'Request Subtype',
                  size: 160,
              } as ColumnDef<OpenRequestSummary>,
          ]
        : []),
    {
        accessorKey: 'createDate',
        header: 'Created On',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'lastUpdated',
        header: 'Last Updated',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'resolvedDate',
        header: 'Resolved Date',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'submitterUpn',
        header: 'Created By',
        size: 150,
        cell: ({ getValue, row }) => (
            <UserCell upn={getValue()} fullName={row.original.submitterFullName} small />
        ),
    },
    {
        accessorKey: 'isUnread',
        header: () => <Icon icon={IconNames.CHAT} />,
        enableSorting: false,
        size: 48,
        cell: ({ getValue }) => <RequestChatIcon isUnread={getValue()} />,
    },
    {
        accessorKey: 'urgent',
        header: () => <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} />,
        enableSorting: false,
        size: 48,
        cell: ({ getValue }) =>
            getValue() ? (
                <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} title="Urgent" />
            ) : null,
    },
    {
        accessorKey: 'requestNumber',
        header: 'Request #',
        size: 90,
        cell: ({ getValue, row }) => (
            <PayerRequestNumber text={getValue()} pbmOnly={row.original.pbmOnly} />
        ),
    },
    {
        accessorKey: 'assignedUpn',
        header: 'FMH Assignee',
        size: 120,
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={
                    getValue() ? (
                        (row.original.assignedFullName ?? getValue())
                    ) : (
                        <span className="sds_request-assigned-to--unassigned">Unassigned</span>
                    )
                }
            />
        ),
    },
    ...(requestType === RequestType.ESCALATION
        ? [
              {
                  accessorKey: 'escalatedTo',
                  header: 'Escalated To',
                  size: 120,
                  cell: ({ getValue }) => {
                      const escalatedTo = getValue();
                      return (
                          <SecondaryData
                              data={
                                  escalatedTo ? EscalatedToDisplayTextMap[escalatedTo] : undefined
                              }
                          />
                      );
                  },
              } as ColumnDef<OpenRequestSummary>,
          ]
        : [
              {
                  accessorKey: 'payerAssignedUpn',
                  header: 'Payer Assignee',
                  size: 120,
                  cell: ({ getValue, row }) => (
                      <SecondaryData
                          data={
                              getValue() ? (
                                  (row.original.payerAssignedFullName ?? getValue())
                              ) : (
                                  <span className="sds_request-assigned-to--unassigned">
                                      Unassigned
                                  </span>
                              )
                          }
                      />
                  ),
              } as ColumnDef<OpenRequestSummary>,
          ]),
    {
        accessorKey: 'requestStatus',
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => (
            <PayerRequestStatusIndicator
                status={
                    getValue() === PayerRequestStatusIndicator.Statuses.RESOLVED
                        ? PayerRequestStatusIndicator.Statuses.MONITORING
                        : getValue()
                }
            />
        ),
    },
];

export const RequestsGrid = ({ requestType }: RequestsGridProps) => {
    const navigate = useNavigate();
    const { segmentIds } = useAppSelector((state) => state.auth);
    const { payerId, segmentId } = useAppSelector((state) => state.views);
    const { showPayerRequestSubSubTypes } = useFlags<{
        showPayerRequestSubSubTypes: boolean;
    }>();

    const { data, isFetching } = useGetOpenRequestsByTypeQuery(
        payerId
            ? {
                  payerId,
                  requestType,
                  segmentIds: segmentId ? [segmentId] : segmentIds,
              }
            : skipToken,
    );
    const userNameMap = useUserNameMap(payerId);
    const requests = useMemo(
        () =>
            data?.map((request) => ({
                ...request,
                submitterFullName: userNameMap?.[request.submitterUpn],
                assignedFullName: request.assignedUpn && userNameMap?.[request.assignedUpn],
                payerAssignedFullName:
                    request.payerAssignedUpn && userNameMap?.[request.payerAssignedUpn],
            })),
        [data, userNameMap],
    );

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(requestType, initialSortProperties);

    return (
        <GridWithFilters
            viewsPaneContent={<ViewsControls requestType={requestType} />}
            data={requests}
            isLoading={isFetching}
            columns={columns({ requestType }, showPayerRequestSubSubTypes)}
            sortProperties={sort}
            onSort={updateGridSort}
            enableSearch
            searchText={search}
            onSearch={updateGridSearch}
            customSearchValueHandlers={customSearchValueHandlers}
            filterCategories={filterCategories({ requestType }, showPayerRequestSubSubTypes)}
            activeFilters={activeFilters}
            onFilter={updateGridActiveFilters}
            filterPaneRenderWidth={filterPaneWidth}
            onFilterPaneResize={updateFilterPaneWidth}
            onRowClick={(rowData) =>
                navigate(AppRoutes.requestDetails(rowData.payerId, rowData.requestId))
            }
        />
    );
};
